import * as React from "react";
import { useState, useRef, useEffect } from "react";
import Select from "react-select";
import { Overlay, Tooltip, Dropdown } from "react-bootstrap";

import * as sf from "@/lib/stockForecast";

import { getNumberOfShifts, niceDate, weekNumber, setIntersection } from "@/lib/util";

import { translate } from "@/modules/shared/IntlProviderWrapper";
import { translateAggregationTimeWindow } from "./MaterialStockForecast";
import MultiSelect from "@/modules/shared/ui/forms/MultiSelect";

import type { StockForecastFilterProps } from "./MaterialStockForecastView";
import type { ILineData } from "@/lib/backend/manufacturing2.generated.types";
import type { AggregationTimeWindow } from "./MaterialStockForecast";

type DayHeader = {
    date: string;
    day: string;
    col_width: number;
};

type BaseProps = {
    day_headers: DayHeader[];
    stickyOffsetTop: number;
};

type Props = {
    selected_aggregate?: string;
    onTitleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onMaterialTypeChange?: (values: Set<string>) => void;
    shift_headers: sf.MaterialForecastShift[];
    stickyOffsetTop: number;
    show_affiliated_stock: boolean;
    safety_stock_show: boolean;
    handleStockForecastDisplayTypeChange?: (value: string) => void;
    changeSelectedLineUUID?: (value: string) => void;
    onChangeOrderExternalId?: (value: string) => void;
    changeSelectedHorizonStockForecast?: (value: AggregationTimeWindow) => void;
    stock_forecast_filter?: StockForecastFilterProps;
    show_toolbox?: boolean;
    hide_lines_filter?: boolean;
    hide_display_type_filter?: boolean;
    material_types?: Set<string>;
    show_material_type?: boolean;
    selected_material_types?: Set<string>;
} & BaseProps;

type ShiftProps = {
    shift_headers: sf.MaterialForecastShift[];
} & BaseProps;

const ShiftAggregation = (props: ShiftProps) => {
    const { shift_headers, day_headers } = props;

    const shifts_per_day = getNumberOfShifts(1);
    const start_shift = shift_headers[0].shift ?? 0;
    const first_col_span = shifts_per_day - start_shift;
    const last_col_span = start_shift || shifts_per_day;

    return (
        <>
            {day_headers.map((dh, idx) => {
                let col_span = shifts_per_day;

                if (idx === 0) {
                    col_span = first_col_span;
                }

                if (idx === day_headers.length - 1) {
                    col_span = last_col_span;
                }

                return (
                    <th
                        key={idx + "_day_header"}
                        colSpan={col_span}
                        rowSpan={1}
                        className="day-end date"
                        style={{ top: props.stickyOffsetTop }}
                    >
                        <span>{niceDate(new Date(dh.date))}</span> <br></br>
                        <span>{dh.day}</span>
                    </th>
                );
            })}
        </>
    );
};

const DayAggregation = (props: BaseProps) => {
    const { day_headers } = props;

    return (
        <>
            {day_headers.map((dh, idx) => {
                return (
                    <th
                        key={idx + "_day_header"}
                        colSpan={3}
                        rowSpan={2}
                        className="day-end date"
                        style={{ top: props.stickyOffsetTop }}
                    >
                        <span>{niceDate(new Date(dh.date))}</span> <br></br>
                        <span id="day-header">{dh.day}</span>
                    </th>
                );
            })}
        </>
    );
};

const WeekAggregation = (props: BaseProps) => {
    const { day_headers } = props;

    // Group dates by weeks
    const week_number_to_dates_map: Map<number, string[]> = new Map();
    for (const header of day_headers) {
        const date = new Date(header.date);
        const week_number = weekNumber(date);
        let dates = week_number_to_dates_map.get(week_number);
        if (dates === undefined) {
            dates = [];
            week_number_to_dates_map.set(week_number, dates);
        }

        dates.push(niceDate(date));
    }

    return (
        <>
            {[...week_number_to_dates_map.entries()].map(([week_number, dates]) => (
                <th
                    key={week_number + "_day_header"}
                    colSpan={3}
                    rowSpan={2}
                    className="day-end date"
                    style={{ top: props.stickyOffsetTop }}
                >
                    <span>
                        {dates[0]} - {dates[dates.length - 1]}
                    </span>{" "}
                    <br />
                    <span>
                        {translate("common.week", "Week")} {week_number}
                    </span>
                </th>
            ))}
        </>
    );
};

type ToolBoxProps = {
    stock_forecast_filter?: StockForecastFilterProps;
    handleStockForecastDisplayTypeChange?: (value: string) => any;
    changeSelectedLineUUID?: (value: string) => any;
    onChangeOrderExternalId?: (value: string) => any;
    hide_display_type_filter?: boolean;
    hide_lines_filter?: boolean;
};

const SelectStyle = {
    control: base => ({ ...base, display: "none" }),
    menu: base => ({ ...base, boxShadow: "none", width: "200px" }),
    menuList: base => ({ ...base, width: "200px" }),
};

type Option = {
    code: string;
    title: string;
};
type SelectOption = {
    label: string;
    value: string;
};

const mapDisplayTypes = (options: Option[]): SelectOption[] => {
    return options.map(o => ({ label: o.title, value: o.code }));
};

const mapLines = (lines: ILineData[]): SelectOption[] => {
    const all_lines_value = {
        label: translate("Manufacturing.LinesView.all_lines", "All lines"),
        value: "",
    };
    return [all_lines_value, ...lines.map(l => ({ label: l.title, value: l.uuid }))];
};

const TOOLBOX_DISPLAY_TYPE = "display_type";
const TOOLBOX_SELECTED_LINE = "selected_line";
const TOOLBOX_WORKORDER = "work_order";

type OpenMenuType = typeof TOOLBOX_DISPLAY_TYPE | typeof TOOLBOX_SELECTED_LINE | typeof TOOLBOX_WORKORDER;

type TooltipSearchOrderProps = {
    tooltip_ref: HTMLElement;
    open_menu: OpenMenuType | null;
    setStateOpenMenu: (value: OpenMenuType | null) => void;
    onChange: (value: string) => void;
    order_external_ids?: string[];
};

const TooltipSearchOrder = (props: TooltipSearchOrderProps) => {
    const { tooltip_ref, open_menu, order_external_ids } = props;

    const onChangeInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.currentTarget instanceof HTMLInputElement) {
            setStateInputValue(event.currentTarget.value);
        }
    };

    const [input_value, setStateInputValue] = useState("");

    useEffect(() => {
        setStateInputValue(order_external_ids && order_external_ids.length > 0 ? order_external_ids.join(",") : "");
    }, [props.order_external_ids]);

    return (
        <Overlay target={tooltip_ref} show={open_menu === TOOLBOX_WORKORDER} placement="right">
            <Tooltip id="stock-forecast-toolbox-tooltip" style={{ zIndex: "999" }}>
                <input
                    type="text"
                    className="form-control search_bar search_bar_small"
                    placeholder={translate(
                        "Manufacturing.StockStatusView.search_by_order_external_id",
                        "Filter by work order"
                    )}
                    name="order_external_id_filter"
                    value={input_value}
                    style={{ marginRight: "15px", minWidth: "200px" }}
                    onChange={onChangeInputValue}
                    onBlur={ev => {
                        ev.currentTarget.focus();
                    }}
                />
                <button className="btn btn-primary" onClick={() => props.onChange(input_value)}>
                    {translate("common.search", "Search")}
                </button>
            </Tooltip>
        </Overlay>
    );
};

const handleSelectChange = (value: string, cb) => {
    if (!cb) return;
    cb(value);
};

const isOutsideClick = (target: HTMLElement) => {
    return !target.closest("#stock-forecast-toolbox") && !target.closest("#stock-forecast-toolbox-tooltip");
};

const ToolBox = (props: ToolBoxProps) => {
    const tooltip_ref = useRef<HTMLDivElement | null>(null);
    const [open_menu, setStateOpenMenu] = useState<OpenMenuType | null>(null);

    const onOpenMenu = (type: OpenMenuType) => () =>
        open_menu === type ? setStateOpenMenu(null) : setStateOpenMenu(type);

    useEffect(() => {
        const listener = (event: MouseEvent) => {
            if (isOutsideClick(event.target as HTMLElement)) {
                setStateOpenMenu(null);
            }
        };

        document.body.addEventListener("click", listener);
        return () => document.body.removeEventListener("click", listener);
    }, []);

    if (!props.stock_forecast_filter) return null;

    const { display_types, lines, order_external_ids, selected_display_type, selected_line_uuid } =
        props.stock_forecast_filter;

    const getFocus = type => (open_menu === type ? "focus" : "");

    const optionsDisplayTypes = display_types ? mapDisplayTypes(display_types) : [];
    const optionsLines = lines && lines.length > 0 ? mapLines(lines) : [];

    return (
        <div id="stock-forecast-toolbox">
            <div id="icon-container">
                {!props.hide_display_type_filter && (
                    <>
                        <i
                            onClick={onOpenMenu(TOOLBOX_DISPLAY_TYPE)}
                            className={`fas fa-box-open fa-2x ${getFocus(TOOLBOX_DISPLAY_TYPE)}`}
                            title={translate("common.tooltip_show_input_produced", "Show input or produced materials")}
                        ></i>
                        <Select
                            id={"selected-display-type"}
                            styles={SelectStyle}
                            options={optionsDisplayTypes}
                            menuIsOpen={open_menu === TOOLBOX_DISPLAY_TYPE}
                            value={optionsDisplayTypes.find(dt => dt.value === selected_display_type)}
                            onChange={(selected: SelectOption) =>
                                handleSelectChange(selected.value, props.handleStockForecastDisplayTypeChange)
                            }
                        />
                    </>
                )}
            </div>

            <div id="icon-container">
                {!props.hide_lines_filter && (
                    <>
                        <i
                            className={`fas fa-bars fa-2x ${getFocus(TOOLBOX_SELECTED_LINE)}`}
                            onClick={onOpenMenu(TOOLBOX_SELECTED_LINE)}
                            title={translate("common.tooltip_filter_production_lines", "Filter by production lines")}
                        ></i>
                        <Select
                            id={"selected-line"}
                            styles={SelectStyle}
                            options={optionsLines}
                            onChange={(selected: SelectOption) =>
                                handleSelectChange(selected.value, props.changeSelectedLineUUID)
                            }
                            value={optionsLines.find(l => l.value === selected_line_uuid)}
                            menuIsOpen={open_menu === TOOLBOX_SELECTED_LINE}
                        />
                    </>
                )}
            </div>

            <div ref={tooltip_ref}>
                <i
                    className={`fas fa-file-alt fa-2x ${getFocus(TOOLBOX_WORKORDER)}`}
                    onClick={onOpenMenu(TOOLBOX_WORKORDER)}
                    title={translate("common.tooltip_filter_production_orders", "Filter by production orders")}
                ></i>
                {props.onChangeOrderExternalId && tooltip_ref.current && (
                    <TooltipSearchOrder
                        tooltip_ref={tooltip_ref.current}
                        open_menu={open_menu}
                        setStateOpenMenu={setStateOpenMenu}
                        order_external_ids={order_external_ids}
                        onChange={props.onChangeOrderExternalId}
                    />
                )}
            </div>
        </div>
    );
};

type MaterialTitleProps = {
    title: string;
    onTitleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
type MaterialTitleState = {
    title: string;
};
class MaterialTitle extends React.Component<MaterialTitleProps, MaterialTitleState> {
    state: MaterialTitleState = {
        title: "",
    };

    componentDidMount() {
        this.setState({
            title: this.props.title,
        });
    }

    onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.currentTarget instanceof HTMLInputElement) {
            // read new values
            const target = event.currentTarget;
            const value = target.value;

            this.setState({
                title: value,
            });

            if (this.props.onTitleChange) {
                this.props.onTitleChange(event);
            }
        }
    };

    render() {
        if (!this.props.onTitleChange) {
            return <span className="material-title">{translate("common.material", "Material")}</span>;
        }

        return (
            <input
                type="text"
                className="form-control"
                name={"input_title"}
                value={this.state.title}
                onChange={this.onChange}
                style={{ opacity: "0.5", minWidth: "100px" }}
                placeholder={translate("common.material", "Material")}
            />
        );
    }
}

type MaterialTypeSelectorProps = {
    material_types?: Set<string>;
    selected_material_types?: Set<string>;
    onMaterialTypeChange?: (values: Set<string>) => void;
};

const MaterialTypeSelector = (props: MaterialTypeSelectorProps) => {
    const { material_types, selected_material_types, onMaterialTypeChange } = props;

    const handleChange = (selected_values: SelectOption[]) => {
        if (onMaterialTypeChange) {
            onMaterialTypeChange(new Set(selected_values.map(item => item.value)));
        }
    };

    if (!material_types || material_types.size === 0) {
        return null;
    }

    const options = [...material_types].map(value => ({
        label: value,
        value,
    }));

    const selectedInMaterialTypes = setIntersection(material_types, selected_material_types);
    const selected_values = [...selectedInMaterialTypes].map(value => ({
        label: value,
        value,
    }));

    return (
        <div style={{ minWidth: "150px" }} className="text-left">
            <MultiSelect
                options={options}
                value={selected_values}
                onChange={handleChange}
                placeholder={translate("common.type", "Type")}
                show_count={true}
            />
        </div>
    );
};

type AggregationDropdownProps = {
    selected_aggregate: string;
    onAggreagationChange: (value: AggregationTimeWindow) => void;
};

const AggregationDropdown = (props: AggregationDropdownProps) => {
    return (
        <div className="ml-2">
            <Dropdown>
                <Dropdown.Toggle
                    as="button"
                    className="btn btn-outline-secondary"
                    type="button"
                    id="aggregationDropdownMenuButton"
                >
                    {translateAggregationTimeWindow(props.selected_aggregate)}
                    <i className="fas fa-chevron-down" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <Dropdown.Item
                        as="a"
                        active={props.selected_aggregate === "shift"}
                        onClick={() => props.onAggreagationChange("shift")}
                    >
                        {translateAggregationTimeWindow("shift")}
                    </Dropdown.Item>
                    <Dropdown.Item
                        as="a"
                        active={props.selected_aggregate === "day"}
                        onClick={() => props.onAggreagationChange("day")}
                    >
                        {translateAggregationTimeWindow("day")}
                    </Dropdown.Item>
                    <Dropdown.Item
                        as="a"
                        active={props.selected_aggregate === "week"}
                        onClick={() => props.onAggreagationChange("week")}
                    >
                        {translateAggregationTimeWindow("week")}
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

const MaterialStockForecastHeader = (props: Props) => {
    const {
        day_headers,
        shift_headers,
        onTitleChange,
        onMaterialTypeChange,
        selected_aggregate,
        show_affiliated_stock,
        safety_stock_show,
        stock_forecast_filter,
        material_types,
        show_material_type = false,
        selected_material_types,
    } = props;

    if (!selected_aggregate || day_headers.length === 0) return null;

    const title = (stock_forecast_filter && stock_forecast_filter.title) || "";

    let ShiftHeaderComponent: React.ReactNode;
    let DayHeaderComponent: React.ReactNode;

    if (selected_aggregate === "shift") {
        DayHeaderComponent = (
            <ShiftAggregation
                day_headers={day_headers}
                shift_headers={shift_headers}
                stickyOffsetTop={props.stickyOffsetTop}
            />
        );

        const shift_names = ["1", "2", "3"];

        ShiftHeaderComponent = shift_headers.map((x, idx) => (
            <th
                key={idx + "_shift_headers"}
                id="shift-number"
                className={x.shift === 2 ? "day-end" : ""}
                style={{ top: props.stickyOffsetTop + 40 }}
            >
                {shift_names[x.shift]}
            </th>
        ));
    }

    let sticky_stock_status_offset = 40;

    if (selected_aggregate === "day") {
        DayHeaderComponent = <DayAggregation day_headers={day_headers} stickyOffsetTop={props.stickyOffsetTop} />;
        sticky_stock_status_offset = 14;
    }

    if (selected_aggregate === "week") {
        DayHeaderComponent = <WeekAggregation day_headers={day_headers} stickyOffsetTop={props.stickyOffsetTop} />;
        sticky_stock_status_offset = 14;
    }

    return (
        <thead>
            <tr>
                <th
                    rowSpan={2}
                    colSpan={show_material_type ? 2 : 1}
                    className="day-end date"
                    style={{ top: props.stickyOffsetTop }}
                >
                    <div className="stock-forecast-input-and-toolbox">
                        <MaterialTitle onTitleChange={onTitleChange} title={title} />
                        {show_material_type && (
                            <MaterialTypeSelector
                                material_types={material_types}
                                selected_material_types={selected_material_types}
                                onMaterialTypeChange={onMaterialTypeChange}
                            />
                        )}
                        {props.changeSelectedHorizonStockForecast && (
                            <AggregationDropdown
                                selected_aggregate={selected_aggregate}
                                onAggreagationChange={props.changeSelectedHorizonStockForecast}
                            />
                        )}

                        {props.show_toolbox && (
                            <ToolBox
                                key={"toolbox"}
                                stock_forecast_filter={props.stock_forecast_filter}
                                handleStockForecastDisplayTypeChange={props.handleStockForecastDisplayTypeChange}
                                changeSelectedLineUUID={props.changeSelectedLineUUID}
                                onChangeOrderExternalId={props.onChangeOrderExternalId}
                                hide_display_type_filter={props.hide_display_type_filter}
                                hide_lines_filter={props.hide_lines_filter}
                            />
                        )}
                    </div>
                </th>

                <th className="day-end date" style={{ top: props.stickyOffsetTop, height: "15px" }}></th>
                {show_affiliated_stock && (
                    <th className="day-end date" style={{ top: props.stickyOffsetTop, height: "15px" }}></th>
                )}
                {safety_stock_show && (
                    <th className="day-end date" style={{ top: props.stickyOffsetTop, height: "15px" }}></th>
                )}
                {DayHeaderComponent}
            </tr>
            <tr>
                <th
                    id="stock-status"
                    className="day-end"
                    style={{ top: props.stickyOffsetTop + sticky_stock_status_offset, zIndex: 0 }}
                >
                    {translate("Manufacturing.StocksView.stock_status", "Stock Status")}
                </th>
                {show_affiliated_stock && (
                    <th
                        id="affiliated-stock-status"
                        className="day-end"
                        style={{ top: props.stickyOffsetTop + sticky_stock_status_offset }}
                    >
                        {translate("Manufacturing.MaterialStockForecast.affiliated_stock_status", "Affiliated stock")}
                    </th>
                )}
                {safety_stock_show && (
                    <th
                        id="affiliated-stock-status"
                        className="day-end"
                        style={{ top: props.stickyOffsetTop + sticky_stock_status_offset }}
                    >
                        {translate("Manufacturing.MaterialStockForecast.safety_stock_status", "Safety stock")}
                    </th>
                )}

                {ShiftHeaderComponent}
            </tr>
        </thead>
    );
};

export default MaterialStockForecastHeader;
